/**
 * After a game: its recap (MVP, highlights, everyone's stats) and its replay, a step-through of
 * the game rebuilt from its log (`replayFrames`). Both load the finished game's log
 * (`GET /api/games/:id/log`); a replay link (`/replay/<id>`) opens the replay for any signed-in
 * player. Opened from the game over screen, the finished table, and Your Recent Games.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  recapOf,
  replayFrames,
  t,
  tn,
  type Caption,
  type GameLogDetail,
  type GameLogPlayer,
  type PlayerId,
  type ReplayFrame,
  type ReplaySeat,
} from '@kadice/shared';
import { ApiError, api } from '../api';
import { cardImage } from '../cards';
import { shareLink, siteLink } from '../share';
import { useFadeOut } from '../useFadeOut';
import type { GameClient } from '../useGameClient';
import { Avatar } from './Avatar';
import { Dice } from './Dice';

export type ReviewMode = 'recap' | 'replay';

/** The log is written just after the game ends: right at game over it may not be there yet. */
const LOG_RETRIES = 3;
const LOG_RETRY_MS = 1500;

/** A caption's text. */
const say = (c: Caption) => (c.n === undefined ? t(c.key, c.vars) : tn(c.key, c.n, c.vars));

function useGameLog(gameId: number): { log: GameLogDetail | null; failed: boolean } {
  const [state, setState] = useState<{ log: GameLogDetail | null; failed: boolean }>({ log: null, failed: false });
  useEffect(() => {
    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const load = (tries: number) =>
      api.gameLog(gameId).then(
        (log) => live && setState({ log, failed: false }),
        (err: unknown) => {
          if (!live) return;
          if (err instanceof ApiError && err.status === 404 && tries > 0) {
            timer = setTimeout(() => void load(tries - 1), LOG_RETRY_MS);
          } else setState({ log: null, failed: true });
        },
      );
    void load(LOG_RETRIES);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [gameId]);
  return state;
}

/** Share a replay link, with a notice saying what happened. */
async function shareReplay(client: GameClient, gameId: number) {
  const result = await shareLink(siteLink(`/replay/${gameId}`), t('replay.shareTitle'));
  if (result === 'copied') client.notify({ kind: 'success', text: t('replay.linkCopied') });
  if (result === 'failed') client.notify({ kind: 'error', text: t('replay.linkFailed') });
}

/** The recap or the replay of one finished game, switching between them. */
export function Review({
  client,
  gameId,
  mode,
  onMode,
  onClose,
}: {
  client: GameClient;
  gameId: number;
  mode: ReviewMode;
  onMode: (mode: ReviewMode) => void;
  onClose: () => void;
}) {
  const { log, failed } = useGameLog(gameId);
  const body = failed ? (
    <p className="muted">{t('replay.failed')}</p>
  ) : !log ? (
    <p className="muted">{t('replay.loading')}</p>
  ) : mode === 'recap' ? (
    <RecapBody log={log} />
  ) : (
    <ReplayBody log={log} />
  );
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className={`modal review ${mode}`}
        role="dialog"
        aria-modal="true"
        aria-label={t(mode === 'recap' ? 'recap.title' : 'replay.title')}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="row review-head">
          <h3>{t(mode === 'recap' ? 'recap.title' : 'replay.title')}</h3>
          <span className="spacer" />
          <button onClick={() => void shareReplay(client, gameId)} title={t('replay.shareHint')}>
            {t('replay.share')}
          </button>
          <button onClick={() => onMode(mode === 'recap' ? 'replay' : 'recap')}>
            {t(mode === 'recap' ? 'recap.watchReplay' : 'replay.showRecap')}
          </button>
          <button onClick={onClose}>{t('common.close')}</button>
        </div>
        {body}
      </div>
    </div>
  );
}

function RecapBody({ log }: { log: GameLogDetail }) {
  const recap = useMemo(() => recapOf(log), [log]);
  const mvp = recap.players.find((p) => p.id === recap.mvpId);
  return (
    <div className="recap">
      <p className="recap-result">
        {log.winnerName ? t('recap.winner', { name: log.winnerName }) : t('recap.noWinner')}
        <span className="muted small">
          {' '}
          {tn('recap.rounds', log.stats.rounds)} · {formatLength(log.stats.durationMs)}
        </span>
      </p>
      {mvp && (
        <div className="recap-mvp">
          <span className="recap-mvp-tag">{t('recap.mvp')}</span>
          {mvp.bot ? <img src="/img/avatars/bot.svg" alt="" className="avatar" /> : <Avatar playerId={mvp.id} />}
          <strong>{mvp.name}</strong>
          <span className="muted">{tn('recap.kills', mvp.kills)}</span>
        </div>
      )}
      {recap.highlights.length > 0 && (
        <ul className="recap-highlights">
          {recap.highlights.map((h, i) => (
            <li key={i}>{say(h)}</li>
          ))}
        </ul>
      )}
      <div className="recap-table-wrap">
        <table className="recap-table">
          <thead>
            <tr>
              <th>{t('recap.col.player')}</th>
              <th>{t('recap.col.place')}</th>
              <th>{t('recap.col.kills')}</th>
              <th>{t('recap.col.lost')}</th>
              <th>{t('recap.col.wars')}</th>
              <th>{t('recap.col.powers')}</th>
            </tr>
          </thead>
          <tbody>
            {recap.players.map((p) => (
              <tr key={p.id} className={p.id === recap.mvpId ? 'mvp' : undefined}>
                <td>
                  {p.name}
                  {p.forfeited && <span className="muted small"> {t('recap.forfeited')}</span>}
                </td>
                <td>{p.placement}</td>
                <td>{p.kills}</td>
                <td>{p.cardsLost}</td>
                <td>
                  {p.warsWon}/{p.warsFought}
                </td>
                <td>{p.powersUsed}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** '12:05' or '1:02:09'. */
function formatLength(ms: number): string {
  const s = Math.round(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s % 60)}` : `${m}:${pad(s % 60)}`;
}

const NO_IDS: readonly PlayerId[] = [];
/** Seats sitting out a war fade out (and back in) this long. */
const WAR_FADE_MS = 300;

/** Autoplay speeds: ms per move. */
const SPEEDS = [1600, 800, 400] as const;

function ReplayBody({ log }: { log: GameLogDetail }) {
  const frames = useMemo(() => replayFrames(log), [log]);
  const roster = useMemo(() => new Map(log.roster.map((p) => [p.id, p])), [log]);
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(0);
  const last = frames.length - 1;
  const at = Math.min(i, Math.max(0, last));
  const frame = frames[at];
  // A war: everyone not fighting fades out, then leaves the row so the fighters get the room.
  const warOut = useFadeOut(
    frame && frame.warIds.length > 0
      ? frame.seats.filter((s) => !frame.warIds.includes(s.id)).map((s) => s.id)
      : NO_IDS,
    WAR_FADE_MS,
  );

  // Autoplay: one move per tick; it stops by itself at the end.
  const running = playing && at < last;
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setI((n) => n + 1), SPEEDS[speed]);
    return () => clearInterval(id);
  }, [running, speed]);

  if (!frame) return <p className="muted">{t('replay.empty')}</p>;
  const go = (n: number) => {
    setPlaying(false);
    setI(Math.max(0, Math.min(last, n)));
  };
  // Back: to the start of this round, or of the one before when already there. Forward: the next round.
  const roundStart = (dir: -1 | 1) => {
    if (dir > 0) {
      const next = frames.findIndex((f, k) => k > at && f.round > frame.round);
      return go(next === -1 ? last : next);
    }
    const start = frames.findIndex((f) => f.round === frame.round);
    if (start < at) return go(start);
    const prev = frames.findIndex((f) => f.round === frame.round - 1);
    go(prev === -1 ? 0 : prev);
  };

  return (
    <div className="replay">
      <div className={`replay-seats${warOut.gone.length > 0 ? ' war' : ''}`}>
        {frame.seats
          .filter((s) => !warOut.gone.includes(s.id))
          .map((s) => (
            <ReplaySeatView
              key={s.id}
              seat={s}
              player={roster.get(s.id)}
              frame={frame}
              fade={warOut.fading.includes(s.id) ? 'out' : warOut.back.includes(s.id) ? 'in' : undefined}
            />
          ))}
      </div>
      <p className="replay-caption" aria-live="polite">
        {frame.round > 0 && <span className="replay-round">{t('replay.roundTag', { round: frame.round })}</span>}
        {say(frame.caption)}
      </p>
      <div className="replay-controls">
        <button onClick={() => roundStart(-1)} title={t('replay.prevRound')} aria-label={t('replay.prevRound')}>
          ⏮
        </button>
        <button onClick={() => go(at - 1)} disabled={at === 0} title={t('replay.prev')} aria-label={t('replay.prev')}>
          ◀
        </button>
        <button
          className="primary"
          onClick={() => {
            if (running) return setPlaying(false);
            if (at >= last) setI(0);
            setPlaying(true);
          }}
          aria-label={t(running ? 'replay.pause' : 'replay.play')}
          title={t(running ? 'replay.pause' : 'replay.play')}
        >
          {running ? '⏸' : '▶'}
        </button>
        <button onClick={() => go(at + 1)} disabled={at >= last} title={t('replay.next')} aria-label={t('replay.next')}>
          ▶
        </button>
        <button onClick={() => roundStart(1)} title={t('replay.nextRound')} aria-label={t('replay.nextRound')}>
          ⏭
        </button>
        <button
          onClick={() => setSpeed((s) => (s + 1) % SPEEDS.length)}
          title={t('replay.speedHint')}
          aria-label={t('replay.speedHint')}
        >
          {t('replay.speed', { n: 2 ** speed })}
        </button>
      </div>
      <input
        className="replay-scrub"
        type="range"
        min={0}
        max={last}
        value={at}
        onChange={(e) => go(Number(e.target.value))}
        aria-label={t('replay.scrub')}
      />
      <p className="muted small replay-step">{t('replay.step', { n: at + 1, total: frames.length })}</p>
    </div>
  );
}

function ReplaySeatView({
  seat: s,
  player,
  frame,
  fade,
}: {
  seat: ReplaySeat;
  player: GameLogPlayer | undefined;
  frame: ReplayFrame;
  /** A war: fading out (not fighting) or back in (the war is over). */
  fade?: 'out' | 'in';
}) {
  const classes = ['replay-seat', s.state];
  if (fade) classes.push(`fade-${fade}`);
  if (frame.actorId === s.id) classes.push('actor');
  if (frame.victimIds.includes(s.id)) classes.push('victim');
  if (frame.warIds.includes(s.id)) classes.push('at-war');
  const color = player?.color ?? null;
  return (
    <div className={classes.join(' ')}>
      <div className="replay-name">
        {player?.bot ? <img src="/img/avatars/bot.svg" alt="" className="avatar" /> : <Avatar playerId={s.id} />}
        <span>{s.name}</span>
      </div>
      <div className="replay-card">
        {s.card !== null ? (
          <img src={cardImage(color, s.card)} alt="" />
        ) : (
          <div className="replay-card-empty">{t(`replay.state.${s.state}`)}</div>
        )}
        <span className="replay-hand" title={t('replay.handHint')}>
          {s.handCount}
        </span>
      </div>
      <div className="replay-dice">
        <Dice values={s.dice} small />
      </div>
    </div>
  );
}
